import { z } from 'zod';
import { api } from '@shared/api';
import type { operations } from '@molchanovs/api-contract';

type RelationResponse =
  operations['getTaskExternalTask']['responses'][200]['content']['application/json'];
type PreviewResponse =
  operations['getTaskExternalTaskPreview']['responses'][200]['content']['application/json'];
type AcceptedResponse =
  operations['createTaskExternalTask']['responses'][202]['content']['application/json'];

const errorSchema = z.object({ code: z.string(), message: z.string() }).strict().nullable();
const blockerCodeSchema = z.enum([
  'PAYLOAD_TOO_LARGE',
  'ASSIGNMENT_PROVENANCE_MISSING',
  'CREATOR_UNRESOLVED',
  'HISTORY_INCOMPLETE',
  'PRIVATE_DESTINATION_UNVERIFIED',
  'FILE_POLICY_UNAPPROVED',
  'CONFIGURATION_UNAVAILABLE',
  'ALREADY_LINKED',
]);
const relationSchema: z.ZodType<RelationResponse> = z
  .object({
    data: z
      .object({
        ticketId: z.uuid(),
        state: z.enum(['not-linked', 'pending', 'linked', 'needs-review', 'failed']),
        documentId: z.uuid().nullable(),
        documentUrl: z.url().nullable(),
        initialEventId: z.uuid().nullable(),
        updateState: z.enum(['idle', 'pending', 'failed', 'needs-review']),
        retryEventId: z.uuid().nullable(),
        updatedAt: z.iso.datetime().nullable(),
        error: errorSchema,
        brainfabNavigation: z
          .object({
            source: z.enum(['move', 'bubble']).nullable(),
            documentUrl: z.url().nullable(),
            creationBlocked: z.boolean(),
          })
          .strict()
          .optional(),
      })
      .strict(),
  })
  .strict();
const previewSchema: z.ZodType<PreviewResponse> = z
  .object({
    data: z
      .object({
        ticketId: z.uuid(),
        contractVersion: z.literal('movement_helpdesk.v1'),
        eligible: z.boolean(),
        blockers: z.array(z.object({ code: blockerCodeSchema, message: z.string() }).strict()),
        submission: z
          .object({
            ticket: z.record(z.string(), z.unknown()),
            history: z.array(z.record(z.string(), z.unknown())),
          })
          .strict()
          .nullable(),
      })
      .strict(),
  })
  .strict();
const acceptedSchema: z.ZodType<AcceptedResponse> = z
  .object({
    data: z.object({ ticketId: z.uuid(), eventId: z.uuid(), monitorUrl: z.string() }).strict(),
  })
  .strict();

export type ExternalTaskRelation = RelationResponse['data'];
export type ExternalTaskPreview = PreviewResponse['data'];
const path = (ticketId: string) =>
  `/api/v1/tasks/${encodeURIComponent(z.uuid().parse(ticketId))}/external-task`;

function requiredEtag(headers: Headers): string {
  const etag = headers.get('etag');
  if (!etag || !/^"[\x21\x23-\x7e]+"$/u.test(etag))
    throw new Error('A valid preview or relation ETag is required.');
  return etag;
}

export async function readExternalTask(ticketId: string) {
  const response = await api.getWithMetadata(path(ticketId), { schema: relationSchema });
  return { relation: response.data.data, etag: requiredEtag(response.headers) };
}

export async function previewExternalTask(ticketId: string) {
  const response = await api.getWithMetadata(`${path(ticketId)}/preview`, {
    schema: previewSchema,
  });
  return { preview: response.data.data, etag: requiredEtag(response.headers) };
}

export async function createExternalTask(ticketId: string, etag: string, idempotencyKey: string) {
  return api.post(
    path(ticketId),
    {},
    { schema: acceptedSchema, headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } }
  );
}

export async function retryExternalTask(
  ticketId: string,
  eventId: string,
  etag: string,
  idempotencyKey: string
) {
  return api.post(
    `${path(ticketId)}/retry`,
    { eventId },
    { schema: acceptedSchema, headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } }
  );
}
